import {
	type BreakRulesConfig,
	type DayStats,
	type DayWageStats,
	type Nurse,
	NurseFullRoleShort,
	type NurseInShift,
	NurseRole,
	NurseTrainingStatus,
	type Shift,
	type StatsCalculationParams,
	type WeekData,
} from "./models";
import { getCensus, getShiftWorkHours, getSnfWantedNursesHours, getTotalHours } from "./utilFunctions";
import { isShiftExcludedFromPpd } from "./wageUtils";

/**
 * Shifts that count toward COVERAGE/ratio displays (how many bodies fill a required position).
 * Excludes trainees/orientation and pending-delete shifts, but KEEPS `excludeFromPpd` staff
 * (DON, ADON, …): `excludeFromPpd` is a PPD/cost concept, whereas coverage tracks whether a
 * required role/job slot is filled. Use this for role/job counts; use `filterNursesForStats`
 * for PPD/HPPD/cost stats.
 */
/**
 * The `includeTrainees` answer for every "is this required slot filled?" question.
 *
 * Exported as one named constant rather than a literal at each call site: the Daily Gaps view and
 * the Weekly Gap View once resolved this differently — weekly excluded trainees, the gap finder
 * counted them — and reported different gaps for the same day. Callers asking a coverage question
 * pass this; only surfaces that deliberately show trainees (roster/headcount displays) pass `true`.
 */
export const INCLUDE_TRAINEES_IN_COVERAGE = false;

export function filterNursesForCoverage(nurse: NurseInShift, includeTrainees: boolean): boolean {
	if (
		!includeTrainees &&
		(nurse.trainingStatus === NurseTrainingStatus.Trainee ||
			nurse.trainingStatus === NurseTrainingStatus.Orientation ||
			nurse.trainingStatus === NurseTrainingStatus.Education)
	)
		return false;

	if (nurse.isPendingToDelete) return false;

	return true;
}

export function filterNursesForStats(
	nurse: NurseInShift,
	jobManagementEnabled: boolean,
	allNurses: Nurse[] | undefined,
	includeTrainees: boolean,
	includePpdExcluded: boolean,
): boolean {
	if (!filterNursesForCoverage(nurse, includeTrainees)) return false;

	// DHPPD keeps ppd-excluded staff (DON, ADON, …); NHPPD/standard stats drop them.
	if (!includePpdExcluded) {
		// FF OFF resolves the per-nurse flag from the Nurse profile; resolved lazily so the O(n)
		// lookup is skipped when job management is on (the per-shift flag is authoritative there).
		const nurseLevelExclude = jobManagementEnabled ? undefined : allNurses?.find((n) => n.id === nurse.nurseId)?.excludeFromPpd;
		if (isShiftExcludedFromPpd(jobManagementEnabled, nurse.excludeFromPpd, nurseLevelExclude)) return false;
	}

	return true;
}

export function calculateDayStats(params: StatsCalculationParams, isSnf: boolean, _timezone: string, isActual?: boolean): DayStats {
	const { weeklyShifts, dayIndex, department, rolesToDisplay, wageData, jobManagementEnabled, nurses } = params;

	const weekData: WeekData = {
		weeklyShifts,
		weekScore: "0",
		weekLoad: "0",
		weekSeason: "0",
		workForce: "0",
		loadText: "",
		seasonText: "",
		workForceText: "",
		weekDifficulty: "",
	};

	const census = getCensus(weekData, dayIndex);
	const wantedHours = getSnfWantedNursesHours(weekData, dayIndex, department, census);

	const { totalStaff, totalHours } = getTotalHours(
		department.department_id,
		weeklyShifts,
		dayIndex,
		isActual,
		isSnf,
		rolesToDisplay,
		undefined,
		undefined,
		department.breakRulesConfig ?? [],
		jobManagementEnabled,
		nurses,
		false, // standard day stats exclude trainees/orientation
		false, // and exclude ppd-excluded staff (NHPPD-style)
	);

	// Look up wage stats from the separate wage data endpoint by date key
	const day = weeklyShifts[0]?.days[dayIndex];
	const dateKey = day?.date?.toISODate?.() ?? "";
	const wageStatsMap = isActual ? wageData?.dayWageStatsActual : wageData?.dayWageStats;
	const wageStats: DayWageStats | undefined = dateKey ? wageStatsMap?.[dateKey] : undefined;
	const totalWages = wageStats?.totalSpent ?? 0;
	const traineeWages = wageStats?.traineeCost ?? 0;
	const orientationWages = wageStats?.orientationCost ?? 0;

	const ppd = census > 0 ? Number((totalWages / census).toFixed(2)) : 0;
	const hppd = census > 0 ? Number((totalHours / census).toFixed(2)) : 0;
	const ptoCost = census > 0 ? Number(((wageStats?.ptoCost ?? 0) / census).toFixed(2)) : 0;
	const traineeCost = census > 0 ? Number(((traineeWages + orientationWages) / census).toFixed(2)) : 0;

	return {
		totalStaff,
		census,
		totalHours,
		wantedHours,
		totalSpent: totalWages,
		ppd,
		laborPPD: ppd + traineeCost + ptoCost,
		hppd,
		isActual,
	};
}

export function countDailyHours({
	nurseId,
	departmentId,
	weeklyShifts,
	dayIndex,
	breakRulesConfig = [],
	excludedShiftId,
	nurseUnion,
}: {
	nurseId: number;
	departmentId: string;
	weeklyShifts: Shift[];
	dayIndex: number;
	breakRulesConfig?: BreakRulesConfig[];
	// Edit flows: omit the shift being replaced so its hours aren't summed alongside the new times.
	excludedShiftId?: string;
	nurseUnion?: string | null;
}): number {
	const dayShifts = weeklyShifts.flatMap((shift) => shift.days).filter((day) => day.date.weekday % 7 === dayIndex);
	return dayShifts
		.flatMap((day) => day.nurses)
		.filter((nurse) => nurse.nurseId === nurseId && !nurse.isPending && !nurse.isPendingToDelete && nurse.id !== excludedShiftId)
		.reduce(
			(totalHours, nurse) =>
				totalHours +
				getShiftWorkHours(
					departmentId,
					nurse.endTime,
					nurse.startTime,
					breakRulesConfig,
					NurseRole[NurseFullRoleShort[nurse.role] as keyof typeof NurseRole],
					nurse.type,
					nurse.jobId ? [nurse.jobId] : [],
					undefined,
					nurseUnion,
				),
			0,
		);
}
